// src/pages/Resgates.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import {
  BadgeDollarSign,
  CheckCircle2,
  Clock3,
  FileSpreadsheet,
  Search,
  SlidersHorizontal,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";

import "./Resgates.css";
import ComProtecao from "../components/ComProtecao";

function ResgatesConteudo() {
  const [resgates, setResgates] = useState([]);
  const [observacoes, setObservacoes] = useState({});

  const [filtros, setFiltros] = useState({
    cliente: "",
    cidade: "",
    estado: "",
    empresa: "",
    status: "",
    inicio: "",
    fim: "",
  });

  const [paginaAtual, setPaginaAtual] = useState(1);

  const itensPorPagina = 10;
  const usuarioLogado = JSON.parse(localStorage.getItem("usuario"));

  useEffect(() => {
    carregarResgates();
  }, []);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtros]);

  const carregarResgates = () => {
    axios
      .get("/resgates/agrupados")
      .then((res) => {
        const ordenado = (Array.isArray(res.data) ? res.data : []).sort(
          (a, b) => {
            if (a.status === "pedido" && b.status !== "pedido") return -1;
            if (a.status !== "pedido" && b.status === "pedido") return 1;

            return new Date(b.data_mais_recente) - new Date(a.data_mais_recente);
          }
        );

        setResgates(ordenado);
      })
      .catch((err) =>
        console.error("Erro ao carregar resgates agrupados:", err)
      );
  };

  const limparObservacoes = (grupo) => {
    setObservacoes((prev) => {
      const novo = { ...prev };
      grupo.resgate_ids.forEach((id) => delete novo[id]);
      return novo;
    });
  };

  const aprovarGrupo = async (grupo) => {
    if (
      !window.confirm(
        `Aprovar ${grupo.quantidade}x ${grupo.nome_premio} para ${grupo.nome_usuario}?`
      )
    ) {
      return;
    }

    try {
      for (const id of grupo.resgate_ids) {
        await axios.put(`/resgates/aprovar/${id}`, {
          operador_id: usuarioLogado.id,
          operador_nome: usuarioLogado.nome,
          observacao: observacoes[id] || "",
        });
      }

      toast.success(`Resgate aprovado para ${grupo.nome_usuario}`);
      limparObservacoes(grupo);
      await carregarResgates();
    } catch (err) {
      console.error("Erro ao aprovar grupo:", err);
      toast.error("Erro ao aprovar resgate. Verifique os dados e tente novamente.");
    }
  };

  const rejeitarGrupo = async (grupo) => {
    if (
      !window.confirm(
        `Rejeitar ${grupo.quantidade}x ${grupo.nome_premio} para ${grupo.nome_usuario}?`
      )
    ) {
      return;
    }

    try {
      for (const id of grupo.resgate_ids) {
        await axios.put(`/resgates/rejeitar/${id}`, {
          operador_id: usuarioLogado.id,
          operador_nome: usuarioLogado.nome,
          observacao: observacoes[id] || "",
        });
      }

      toast.success(`Resgate rejeitado para ${grupo.nome_usuario}`);
      limparObservacoes(grupo);
      await carregarResgates();
    } catch (err) {
      console.error("Erro ao rejeitar grupo:", err);
      toast.error("Erro ao rejeitar resgate. Verifique os dados e tente novamente.");
    }
  };

  const exportarExcel = () => {
    const planilha = resgatesFiltrados.map((item) => {
      const aprovador =
        item.nome_aprovador && item.data_aprovacao
          ? `Aprovado por ${item.nome_aprovador} (${new Date(
              item.data_aprovacao
            ).toLocaleString("pt-BR")})`
          : "";

      const recusador =
        item.nome_recusador && item.data_recusa
          ? `Rejeitado por ${item.nome_recusador} (${new Date(
              item.data_recusa
            ).toLocaleString("pt-BR")})`
          : "";

      const log = [aprovador, recusador].filter(Boolean).join(" | ");

      return {
        Cliente: item.nome_usuario,
        Email: item.email,
        Empresa: item.empresa,
        Cidade: item.cidade,
        UF: item.uf,
        Prêmio: item.nome_premio,
        Quantidade: item.quantidade,
        Voltagem: item.voltagem || "-",
        "Pontos Produto (un)": item.pontos_produto_unit,
        "Pontos Frete (un)": item.pontos_frete_unit,
        "Total de Pontos": item.pontos_total,
        Status: item.status,
        IDs: item.resgate_ids.join(", "),
        "Log de Ação": log,
      };
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(planilha);

    XLSX.utils.book_append_sheet(wb, ws, "Resgates");
    XLSX.writeFile(wb, `resgates-${Date.now()}.xlsx`);
  };

  const aplicarFiltros = (item) =>
    (item.nome_usuario || "")
      .toLowerCase()
      .includes((filtros.cliente || "").toLowerCase()) &&
    (item.cidade || "")
      .toLowerCase()
      .includes((filtros.cidade || "").toLowerCase()) &&
    (item.uf || "")
      .toLowerCase()
      .includes((filtros.estado || "").toLowerCase()) &&
    (item.empresa || "")
      .toLowerCase()
      .includes((filtros.empresa || "").toLowerCase()) &&
    (!filtros.inicio ||
      new Date(item.data_mais_recente) >= new Date(filtros.inicio)) &&
    (!filtros.fim ||
      new Date(item.data_mais_recente) <= new Date(filtros.fim)) &&
    (filtros.status === "" || item.status === filtros.status);

  const resgatesFiltrados = useMemo(
    () => resgates.filter(aplicarFiltros),
    [resgates, filtros]
  );

  const resumo = useMemo(() => {
    const total = resgates.length;
    const pendentes = resgates.filter((r) => r.status === "pedido").length;

    const aprovados = resgates.filter(
      (r) =>
        r.status === "preparacao" ||
        r.status === "transporte" ||
        r.status === "enviado"
    ).length;

    const rejeitados = resgates.filter((r) => r.status === "rejeitado").length;

    const pontos = resgatesFiltrados.reduce(
      (acc, item) => acc + Number(item.pontos_total || 0),
      0
    );

    return {
      total,
      pendentes,
      aprovados,
      rejeitados,
      pontos,
    };
  }, [resgates, resgatesFiltrados]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(resgatesFiltrados.length / itensPorPagina)
  );

  const paginaAtualResgates = resgatesFiltrados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  return (
    <div className="resgatesAdmin-page">
      <div className="resgatesAdmin-topbar">
        <div>
          <span className="resgatesAdmin-kicker">Gestão de resgates</span>

          <h2>Resgates</h2>

          <p>
            Aprove, rejeite e acompanhe pedidos de resgate dos clientes do
            Tikpontos.
          </p>
        </div>

        <button
          type="button"
          className="resgatesAdmin-btn primary"
          onClick={exportarExcel}
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      <div className="resgatesAdmin-cards">
        <div className="resgatesAdmin-card">
          <span>Total de resgates</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="resgatesAdmin-card warning">
          <span>Pendentes</span>
          <strong>{formatarNumero(resumo.pendentes)}</strong>
        </div>

        <div className="resgatesAdmin-card success">
          <span>Aprovados</span>
          <strong>{formatarNumero(resumo.aprovados)}</strong>
        </div>

        <div className="resgatesAdmin-card danger">
          <span>Rejeitados</span>
          <strong>{formatarNumero(resumo.rejeitados)}</strong>
        </div>

        <div className="resgatesAdmin-card value">
          <span>Pontos filtrados</span>
          <strong>{formatarNumero(resumo.pontos)} pts</strong>
        </div>
      </div>

      <div className="resgatesAdmin-filtrosCard">
        <div className="resgatesAdmin-filtrosHeader">
          <div>
            <h3>Filtros</h3>
            <span>Filtre por cliente, empresa, status, localização ou período.</span>
          </div>

          <SlidersHorizontal size={18} />
        </div>

        <div className="resgatesAdmin-filtros">
          <div className="resgatesAdmin-filtro busca">
            <label>Cliente</label>

            <div className="resgatesAdmin-searchInput">
              <Search size={15} />

              <input
                placeholder="Nome do cliente"
                value={filtros.cliente}
                onChange={(e) =>
                  setFiltros({
                    ...filtros,
                    cliente: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <div className="resgatesAdmin-filtro busca">
            <label>Empresa</label>

            <div className="resgatesAdmin-searchInput">
              <Search size={15} />

              <input
                placeholder="Empresa"
                value={filtros.empresa}
                onChange={(e) =>
                  setFiltros({
                    ...filtros,
                    empresa: e.target.value,
                  })
                }
              />
            </div>
          </div>

          <div className="resgatesAdmin-filtro">
            <label>Cidade</label>

            <input
              placeholder="Cidade"
              value={filtros.cidade}
              onChange={(e) =>
                setFiltros({
                  ...filtros,
                  cidade: e.target.value,
                })
              }
            />
          </div>

          <div className="resgatesAdmin-filtro">
            <label>UF</label>

            <input
              placeholder="UF"
              value={filtros.estado}
              onChange={(e) =>
                setFiltros({
                  ...filtros,
                  estado: e.target.value,
                })
              }
            />
          </div>

          <div className="resgatesAdmin-filtro">
            <label>Data início</label>

            <input
              type="date"
              value={filtros.inicio}
              onChange={(e) =>
                setFiltros({
                  ...filtros,
                  inicio: e.target.value,
                })
              }
            />
          </div>

          <div className="resgatesAdmin-filtro">
            <label>Data fim</label>

            <input
              type="date"
              value={filtros.fim}
              onChange={(e) =>
                setFiltros({
                  ...filtros,
                  fim: e.target.value,
                })
              }
            />
          </div>

          <div className="resgatesAdmin-filtro">
            <label>Status</label>

            <select
              value={filtros.status}
              onChange={(e) =>
                setFiltros({
                  ...filtros,
                  status: e.target.value,
                })
              }
            >
              <option value="">Todos os status</option>
              <option value="pedido">Pendente</option>
              <option value="preparacao">Preparação</option>
              <option value="transporte">Transporte</option>
              <option value="enviado">Enviado</option>
              <option value="rejeitado">Rejeitado</option>
            </select>
          </div>
        </div>
      </div>

      <div className="resgatesAdmin-tableCard">
        <div className="resgatesAdmin-tableHeader">
          <div>
            <h3>Lista de resgates</h3>

            <span>{formatarNumero(resgatesFiltrados.length)} resultado(s)</span>
          </div>

          <BadgeDollarSign size={18} />
        </div>

        <div className="resgatesAdmin-tableWrapper">
          <table className="resgatesAdmin-table resgatesAdmin-tableCompacta">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Prêmio</th>
                <th>Pontos</th>
                <th>Status / Log</th>
                <th>Observações / Ações</th>
              </tr>
            </thead>

            <tbody>
              {paginaAtualResgates.length === 0 ? (
                <tr>
                  <td colSpan="5" className="resgatesAdmin-empty">
                    Nenhum resgate encontrado.
                  </td>
                </tr>
              ) : (
                paginaAtualResgates.map((g, i) => (
                  <tr key={i}>
                    <td>
                      <div className="resgatesAdmin-cellMain">
                        <strong>{g.nome_usuario || "—"}</strong>
                        <span>{g.email || "—"}</span>
                        <span>{g.empresa || "—"}</span>
                        <small>
                          {[g.cidade, g.uf].filter(Boolean).join(" / ") || "—"}
                        </small>
                      </div>
                    </td>

                    <td>
                      <div className="resgatesAdmin-cellMain">
                        <strong>{g.nome_premio || "—"}</strong>

                        <div className="resgatesAdmin-inlineBadges">
                          <span className="resgatesAdmin-badge">
                            {g.quantidade || 1} un.
                          </span>

                          <span className="resgatesAdmin-badge muted">
                            {g.voltagem || "Sem voltagem"}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="resgatesAdmin-cellMain">
                        <span>
                          Produto:{" "}
                          <strong>
                            {g.pontos_produto_unit?.toLocaleString("pt-BR") || "-"}
                          </strong>
                        </span>

                        <span>
                          Frete:{" "}
                          <strong>
                            {g.pontos_frete_unit?.toLocaleString("pt-BR") || "-"}
                          </strong>
                        </span>

                        <span className="resgatesAdmin-pontosTotal">
                          Total:{" "}
                          {g.pontos_total?.toLocaleString("pt-BR") || "0"} pts
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="resgatesAdmin-cellMain">
                        <span className={`resgatesAdmin-status ${g.status}`}>
                          {g.status}
                        </span>

                        {g.status === "pedido" ? (
                          <div className="resgatesAdmin-log empty compact">
                            <Clock3 size={14} />
                            <span>Pendente</span>
                          </div>
                        ) : g.nome_aprovador && g.data_aprovacao ? (
                          <div className="resgatesAdmin-log success compact">
                            <CheckCircle2 size={14} />

                            <strong>{g.nome_aprovador}</strong>

                            <small>
                              {new Date(g.data_aprovacao).toLocaleString("pt-BR")}
                            </small>
                          </div>
                        ) : g.nome_recusador && g.data_recusa ? (
                          <div className="resgatesAdmin-log danger compact">
                            <ThumbsDown size={14} />

                            <strong>{g.nome_recusador}</strong>

                            <small>
                              {new Date(g.data_recusa).toLocaleString("pt-BR")}
                            </small>
                          </div>
                        ) : (
                          <span className="resgatesAdmin-muted">—</span>
                        )}
                      </div>
                    </td>

                    <td>
                      {g.status === "pedido" ? (
                        <div className="resgatesAdmin-acoes compacta">
                          <textarea
                            className="resgatesAdmin-textarea"
                            placeholder="Observações opcionais"
                            value={observacoes[g.resgate_ids[0]] || ""}
                            onChange={(e) =>
                              setObservacoes({
                                ...observacoes,
                                [g.resgate_ids[0]]: e.target.value,
                              })
                            }
                          />

                          <div className="resgatesAdmin-actionsInline">
                            <button
                              type="button"
                              onClick={() => aprovarGrupo(g)}
                              className="resgatesAdmin-actionBtn approve"
                            >
                              <ThumbsUp size={14} />
                              Aprovar
                            </button>

                            <button
                              type="button"
                              onClick={() => rejeitarGrupo(g)}
                              className="resgatesAdmin-actionBtn reject"
                            >
                              <ThumbsDown size={14} />
                              Rejeitar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <em className="resgatesAdmin-historico">Histórico</em>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="resgatesAdmin-paginacao compacta">
            <button
              type="button"
              onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
              disabled={paginaAtual === 1}
            >
              Anterior
            </button>

            <span>
              Página {paginaAtual} de {totalPaginas}
            </span>

            <button
              type="button"
              onClick={() =>
                setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
              }
              disabled={paginaAtual === totalPaginas}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Resgates() {
  return (
    <ComProtecao rota="resgates" tipo="pode_ler">
      <ResgatesConteudo />
    </ComProtecao>
  );
}